// find-the-bug-grading — the PURE decision logic behind the find-the-bug surface
// (find-the-bug-activity, Trello C902VeMO). No DOM, no network, no runner — every function here
// is a plain transformation, so it is unit-testable directly under Node (the host-only ESM spec
// `test/editor/find-the-bug-grading.test.js`). The chrome (`find-the-bug-chrome.js`) owns the
// runner calls and the DOM; this module owns every RULE.
//
// ── The rules, in one place ───────────────────────────────────────────────────────────────
//
//   1. Row inputs (and, for a return-value compare, the typed expected value) are Python
//      LITERALS parsed by `ast.literal_eval` INSIDE Pyodide — never `eval`, never a JS parser.
//      `buildParseProgram` writes that program; `readParseOutput` reads its answer back.
//        * function target: the parsed value travels to the call as JSON. ACCEPTED LIMIT: JSON
//          has no int/float or tuple/list distinction, so `5.0` arrives as `5` and a tuple as a
//          list — for BOTH the code under test and the hidden reference, identically, so grading
//          stays internally consistent.
//        * stdin target: Python computes `str(value)` itself and THAT string is fed to input(),
//          so Python's own spelling survives exactly (`5.0` -> "5.0", `True` -> "True",
//          `None` -> "None"). This is why the casting lessons use a stdin target.
//
//   2. A row is EVIDENCE of the bug only when its own expectation matches what the hidden
//      reference does for that input AND the code under test does something else. A row whose
//      expectation the reference does NOT meet is a misprediction — never evidence, and never a
//      pass.
//
//   3. A row PASSES only when BOTH halves hold: the code under test meets the typed expectation
//      AND the reference meets it too. The second half is what stops a student "passing" by
//      retyping an expectation to match still-broken code.
//
//   4. `bugFound` is STICKY: once true it stays true (`stickyBugFound`).
//
//   5. Coverage against `minRows` and the boundary check are ADVISORY. `summarise` never reads
//      them; nothing here that decides `bugFound` / `allPass` touches `coverage`.
//
//   6. An "Expect an error" row only checks that SOME exception was raised — never which one.
//      The curriculum asks students to recognise that invalid input makes a program behave as
//      intended (here: stop), not to know Python's exception taxonomy by name.
//
// Nothing here ever returns, formats or stores the REFERENCE's own output. The Actual column is
// always the code under test's.

import { normalizeOutput, valuesMatch, stripPromptLine } from './challenge-grading.js';

export var PARSE_SENTINEL = '@@FTB-PARSE@@';

export var ROW_TYPES = ['expected', 'boundary', 'invalid'];

export var RESULT = {
  PASS: 'pass',
  FAIL: 'fail',
  MISPREDICT: 'mispredict',
  TIMEOUT: 'timeout',
  BAD_LITERAL: 'bad-literal',
  NOT_RUN: ''
};

export var QUOTE_HINT = 'Put text in quotes, like "hello". Numbers, True, False and None need no quotes.';

/**
 * The Python program that parses every raw literal in ONE run. The raw strings travel as a JSON
 * document inside a Python string literal, so nothing the student typed is ever executed: the
 * only evaluation is `ast.literal_eval`, which accepts literals and nothing else.
 *
 * `JSON.stringify(JSON.stringify(list))` is a valid Python string literal: JSON escapes are all
 * legal Python escapes, and JSON.stringify never emits `\/`.
 */
export function buildParseProgram(rawList) {
  var list = (Array.isArray(rawList) ? rawList : []).map(function (r) {
    return (typeof r === 'string') ? r : '';
  });
  return [
    'import ast, json',
    'RAW = json.loads(' + JSON.stringify(JSON.stringify(list)) + ')',
    'out = []',
    'for item in RAW:',
    '    try:',
    '        v = ast.literal_eval(item.strip())',
    '    except Exception as e:',
    '        out.append({"ok": False, "error": type(e).__name__})',
    '        continue',
    '    try:',
    '        j = json.dumps(v)',
    '    except Exception:',
    '        j = None',
    '    out.append({"ok": True, "json": j, "pyType": type(v).__name__, "str": str(v)})',
    'print(' + JSON.stringify(PARSE_SENTINEL) + ' + json.dumps(out))',
    ''
  ].join('\n');
}

/**
 * Read the parse program's answer back. Returns an array (one entry per raw literal) of
 *   { ok:true, hasValue, value, pyType, str }   or   { ok:false, error }
 * or `null` when the sentinel line is missing or unreadable (the parse run itself failed).
 * `hasValue` is false when Python could parse the literal but JSON cannot carry it (a set, a
 * complex number) — usable as stdin text, not as a function argument.
 */
export function readParseOutput(stdout, expectedCount) {
  var text = (stdout == null) ? '' : String(stdout);
  var at = text.lastIndexOf(PARSE_SENTINEL);
  if (at === -1) { return null; }
  var line = text.slice(at + PARSE_SENTINEL.length).split('\n')[0];
  var parsed;
  try { parsed = JSON.parse(line); } catch (e) { return null; }
  if (!Array.isArray(parsed)) { return null; }
  if (typeof expectedCount === 'number' && parsed.length !== expectedCount) { return null; }
  return parsed.map(function (p) {
    if (!p || p.ok !== true) {
      return { ok: false, error: (p && p.error) || 'SyntaxError' };
    }
    var hasValue = typeof p.json === 'string';
    var value;
    if (hasValue) {
      try { value = JSON.parse(p.json); } catch (e) { hasValue = false; }
    }
    return {
      ok: true,
      hasValue: hasValue,
      value: value,
      pyType: String(p.pyType || ''),
      str: String(p.str == null ? '' : p.str)
    };
  });
}

/** A row's raw input cells, trimmed of nothing (the literal parser strips). */
export function rowInputs(row, count) {
  var inputs = (row && Array.isArray(row.inputs)) ? row.inputs.slice(0, count) : [];
  while (inputs.length < count) { inputs.push(''); }
  return inputs.map(function (v) { return (typeof v === 'string') ? v : ''; });
}

/**
 * Normalise one runner function-call result into an OBSERVATION.
 *   { kind:'value', value, jsonSafe, isFloat, stdout } | { kind:'error', type, stdout }
 *   | { kind:'missing' } (the function is not defined)
 */
export function observeFunctionResult(res) {
  if (!res) { return { kind: 'missing' }; }
  if (res.notFound) { return { kind: 'missing' }; }
  if (res.exception) {
    // `text` (pgzero-test-tables, additive): the harness's `format_exception_only` line, which the
    // test table reads to name a game-only object. Find-the-bug ignores it.
    return {
      kind: 'error', type: String(res.exception.type || 'Exception'), stdout: res.stdout || '',
      text: String(res.exception.text || '')
    };
  }
  return {
    kind: 'value',
    value: res.returnValue,
    jsonSafe: res.jsonSafe !== false,
    isFloat: res.isFloat === true || res.returnType === 'float',
    stdout: res.stdout || ''
  };
}

/**
 * The PROGRAM's output from a console run, WITHOUT the echoed input lines. The runner's raw
 * `stdout` carries the prompt AND the echo of every typed value (so input 65 gives "65\n$6\n"),
 * which no prediction can ever match. Mirror Code Challenge's stdin grader
 * (`transcriptResultLines` in challenge-grading.js): each turn's `outputBefore` minus its
 * prompt line, then the trailing `tail`. A result with no `transcript` (a pre-0.13.0 runner)
 * falls back to raw stdout.
 */
export function consoleRunOutput(res) {
  var t = res && res.transcript;
  if (!t || typeof t !== 'object') { return (res && res.stdout) || ''; }
  var turns = Array.isArray(t.turns) ? t.turns : [];
  var parts = turns.map(function (turn) { return stripPromptLine(turn && turn.outputBefore); });
  parts.push(t.tail == null ? '' : String(t.tail));
  return parts.join('');
}

/** Normalise one runner console-run result into an OBSERVATION (stdin target). */
export function observeConsoleRun(res) {
  if (!res) { return { kind: 'missing' }; }
  var out = consoleRunOutput(res);
  if (res.exception) {
    return { kind: 'error', type: String(res.exception.type || 'Exception'), stdout: out };
  }
  return { kind: 'value', stdout: out };
}

/**
 * Does an observation meet a row's expectation?
 *   expectation = { expectError, value, valueIsFloat, text }
 *   compare     = 'return' (function target, the returned value) | 'stdout' (printed text —
 *                 the only choice for a stdin target)
 * An "expect an error" row only checks that SOME exception was raised — never which one. The
 * curriculum this course is built against asks students to test invalid input and recognise
 * that the program behaves as intended (here: it stops), not to know Python's exception
 * taxonomy by name — naming a specific class was a stricter bar than the course requires, and
 * penalised a correct "this crashes" prediction over a misspelled/miscapitalised class name.
 */
export function matchesExpectation(observed, expectation, compare) {
  if (!observed || observed.kind === 'missing' || observed.kind === 'timeout') { return false; }
  var exp = expectation || {};
  if (exp.expectError) {
    return observed.kind === 'error';
  }
  if (observed.kind === 'error') { return false; }
  if (compare === 'return') {
    if (!observed.jsonSafe) { return false; }
    return valuesMatch(observed.value, exp.value, !!exp.valueIsFloat);
  }
  return normalizeOutput(observed.stdout, {}) === normalizeOutput(exp.text, {});
}

/**
 * THE row rule. `cut` / `ref` are the code-under-test and hidden-reference observations (either
 * may be `{kind:'timeout'}`); `badLiteral` short-circuits a row whose input or expected value
 * could not be parsed (no runner call is ever made for it).
 */
export function gradeRow(opts) {
  var o = opts || {};
  if (o.badLiteral) { return RESULT.BAD_LITERAL; }
  if (!o.cut || !o.ref) { return RESULT.NOT_RUN; }
  if (o.cut.kind === 'timeout' || o.ref.kind === 'timeout') { return RESULT.TIMEOUT; }
  var refOK = matchesExpectation(o.ref, o.expectation, o.compare);
  if (!refOK) { return RESULT.MISPREDICT; }
  var cutOK = matchesExpectation(o.cut, o.expectation, o.compare);
  return cutOK ? RESULT.PASS : RESULT.FAIL;
}

/**
 * The two GATING facts, from the row results of ONE run:
 *   bugFound — at least one row is genuine evidence (`fail`)
 *   allPass  — there is at least one row, and EVERY row passes both halves
 * Coverage is deliberately absent: it is advisory and never gates.
 */
export function summarise(results) {
  var list = Array.isArray(results) ? results : [];
  return {
    bugFound: list.some(function (r) { return r === RESULT.FAIL; }),
    allPass: list.length > 0 && list.every(function (r) { return r === RESULT.PASS; })
  };
}

/** Once found, always found. */
export function stickyBugFound(previous, now) {
  return previous === true || now === true;
}

/** May the explanation be submitted? Bug found (ever), the LATEST run all-pass, text present. */
export function canSubmitExplanation(state) {
  var s = state || {};
  return s.bugFound === true && s.allPass === true && s.runFresh === true
    && typeof s.explanation === 'string' && s.explanation.trim() !== '';
}

/** Render a JSON-decoded value the way Python writes it (display only). */
export function formatPyValue(v, isFloat) {
  if (v === null || v === undefined) { return 'None'; }
  if (v === true) { return 'True'; }
  if (v === false) { return 'False'; }
  if (typeof v === 'number') {
    if (isFloat && Number.isInteger(v)) { return v.toFixed(1); }
    return String(v);
  }
  if (typeof v === 'string') {
    return "'" + v.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n') + "'";
  }
  if (Array.isArray(v)) {
    return '[' + v.map(function (x) { return formatPyValue(x, false); }).join(', ') + ']';
  }
  if (typeof v === 'object') {
    return '{' + Object.keys(v).map(function (k) {
      return formatPyValue(k, false) + ': ' + formatPyValue(v[k], false);
    }).join(', ') + '}';
  }
  return String(v);
}

/** The Actual column — ALWAYS the code under test's observation, never the reference's. */
export function formatObserved(observed, compare) {
  if (!observed) { return ''; }
  if (observed.kind === 'timeout') { return 'Did not finish'; }
  if (observed.kind === 'missing') { return 'Function not found'; }
  if (observed.kind === 'error') { return 'Error: ' + observed.type; }
  if (compare === 'return') {
    if (!observed.jsonSafe) { return String(observed.value); }
    return formatPyValue(observed.value, observed.isFloat);
  }
  return normalizeOutput(observed.stdout, {});
}

var NUMERIC = /^[-+]?(\d+(\.\d*)?|\.\d+)$/;

/** Is any NUMERIC raw input within `tol` of any declared boundary? (advisory only) */
export function isNearBoundary(rawInputs, boundaries, tol) {
  var bs = (Array.isArray(boundaries) ? boundaries : []).filter(function (b) {
    return typeof b === 'number' && isFinite(b);
  });
  var t = (typeof tol === 'number' && isFinite(tol) && tol >= 0) ? tol : 0;
  var nums = (Array.isArray(rawInputs) ? rawInputs : []).map(function (r) {
    var s = String(r == null ? '' : r).trim();
    return NUMERIC.test(s) ? Number(s) : null;
  }).filter(function (n) { return n !== null; });
  if (!nums.length) { return null; }   // no numeric input: the label is trusted
  if (!bs.length) { return null; }
  return nums.some(function (n) {
    return bs.some(function (b) { return Math.abs(n - b) <= t; });
  });
}

/**
 * ADVISORY coverage feedback — never gates. Counts each row type against `minRows`, and for a
 * row LABELLED boundary notes when none of its numeric inputs sits near a declared boundary.
 * Returns { counts, shortfalls: [{type, have, want}], offBoundaryRows: [index] }.
 */
export function coverage(rows, cfg) {
  var c = cfg || {};
  var min = c.minRows || {};
  var counts = { expected: 0, boundary: 0, invalid: 0 };
  var offBoundaryRows = [];
  (Array.isArray(rows) ? rows : []).forEach(function (row, i) {
    var type = ROW_TYPES.indexOf(row && row.type) !== -1 ? row.type : 'expected';
    counts[type] += 1;
    if (type === 'boundary') {
      var near = isNearBoundary(row.inputs, c.boundaries, c.boundaryTolerance);
      if (near === false) { offBoundaryRows.push(i); }
    }
  });
  var shortfalls = [];
  ROW_TYPES.forEach(function (t) {
    var want = (typeof min[t] === 'number' && min[t] > 0) ? min[t] : 0;
    if (counts[t] < want) { shortfalls.push({ type: t, have: counts[t], want: want }); }
  });
  return { counts: counts, shortfalls: shortfalls, offBoundaryRows: offBoundaryRows };
}

/** The friendly Result-column label for a row result. Never names a correct value. */
export function resultLabel(result) {
  switch (result) {
    case RESULT.PASS: return 'Pass';
    case RESULT.FAIL: return 'Fail';
    case RESULT.MISPREDICT: return 'Check your prediction';
    case RESULT.TIMEOUT: return 'Timed out';
    case RESULT.BAD_LITERAL: return 'Check the input';
    default: return '—';
  }
}
